import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { customClient } from "@utils/helpers";
import { useCallback, useRef, useState } from "react";

interface UseCursorPaginationParams {
  queryKey: string;
  watchedVariables?: Array<unknown>;
  queryVariables: object;
  document: string;
  responseKey: string;
  pageSize?: number;
  refetchInterval?: number | false | (() => number | false);
}

/**
 * useCursorPagination - Traditional pagination hook with cursor-based navigation
 *
 * This hook is built on top of React Query's useQuery and is designed for
 * traditional pagination patterns where users need to navigate between specific pages.
 *
 * ✅ WHEN TO USE:
 * - Traditional pagination interfaces (tables, search results with page numbers)
 * - Scenarios where users need to jump to specific pages
 * - Memory-conscious applications dealing with large datasets
 * - Bidirectional navigation where users frequently move back and forth
 *
 * ✅ ADVANTAGES:
 * - Fetches and stores only the current page — constant memory usage
 * - Supports bidirectional navigation using cursor history
 * - Better suited for traditional pagination (e.g., paginated tables) where users might jump around
 * - Efficient memory management for large datasets
 * - Clear separation between pages
 *
 * ⚠️ CONSIDERATIONS:
 * - May re-fetch data when navigating back to earlier pages
 * - Loading states occur when switching between pages
 * - Less suitable for infinite scroll patterns
 * - Requires cursor management for efficient navigation
 *
 * @example
 * ```tsx
 * const {
 *   data,
 *   totalItemCount,
 *   totalPages,
 *   pageNumber,
 *   isFetching,
 *   getCurrentPageData,
 *   hasNextPage,
 *   hasPreviousPage
 * } = useCursorPagination({
 *   queryKey: 'users',
 *   queryVariables: { role: 'admin' },
 *   document: GET_USERS_QUERY,
 *   responseKey: 'users',
 *   pageSize: 25
 * });
 *
 * // Usage in paginated table
 * const handlePageChange = (newPage: number) => {
 *   getCurrentPageData(newPage);
 * };
 * ```
 */
export const useCursorPagination = ({
  queryKey,
  watchedVariables = [],
  queryVariables,
  document,
  responseKey,
  pageSize = 10,
  refetchInterval,
}: UseCursorPaginationParams) => {
  const { getAccessTokenSilently } = useAuth0();

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [currentPageSize, setCurrentPageSize] = useState<number>(pageSize);

  // Use ref to track cursors to avoid stale closure
  const cursorsRef = useRef<(string | null)[]>([null]);

  // Compare by content, not reference — watchedVariables is a new array every render.
  const watchedVariablesKey = JSON.stringify(watchedVariables);
  const [previousWatchedVariablesKey, setPreviousWatchedVariablesKey] = useState(watchedVariablesKey);
  if (previousWatchedVariablesKey !== watchedVariablesKey) {
    setPreviousWatchedVariablesKey(watchedVariablesKey);
    cursorsRef.current = [null];
    setPageNumber(1);
  }

  const { data, isFetching, refetch } = useQuery({
    queryKey: [queryKey, pageNumber, currentPageSize, ...watchedVariables],
    queryFn: async () => {
      const token = await getAccessTokenSilently();

      const response = await customClient({
        token,
        document,
        variables: {
          ...queryVariables,
          first: currentPageSize,
          after: cursorsRef.current[pageNumber - 1],
        },
      });

      const responseData = response[responseKey];

      return responseData;
    },
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    staleTime: Infinity,
    refetchInterval,
    gcTime: 0,
  });

  const getCurrentPageData = useCallback(
    (newPage: number, itemsPerPage?: number) => {
      if (newPage === 0) {
        return;
      }

      // If page size changed, reset pagination
      if (itemsPerPage && itemsPerPage !== currentPageSize) {
        cursorsRef.current = [null];
        setCurrentPageSize(itemsPerPage);
        setPageNumber(1);
        return;
      }

      // If going to next page and we need a new cursor
      if (newPage > cursorsRef.current.length && data?.pageInfo?.hasNextPage) {
        // Store the cursor for the next page
        const newCursors = [...cursorsRef.current];
        newCursors[newPage - 1] = data?.pageInfo?.endCursor;
        cursorsRef.current = newCursors;
      }

      // Always update the page number
      setPageNumber(newPage);
    },
    [data?.pageInfo, currentPageSize],
  );

  // Extract the actual data from the current page
  const currentPageData = data?.edges?.map((edge: { node: unknown }) => edge?.node) || [];

  return {
    data: currentPageData,
    totalItemCount: data?.totalCount || 0,
    totalPages: Math.ceil((data?.totalCount || 0) / currentPageSize),
    pageNumber,
    isFetching,
    getCurrentPageData,
    refetch,
    hasNextPage: data?.pageInfo?.hasNextPage || false,
    hasPreviousPage: pageNumber > 1,
  };
};
